import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import { Box, Breadcrumbs, CircularProgress, Typography } from '@mui/material'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  useAttendanceReportData,
  useAttendanceReportItem,
  useDownloadAttendanceReport,
} from '@/shared/api/attendance-report'
import { PERMISSION_SUB_MODULES } from '@/shared/const'
import { CustomButton } from '@/shared/ui/button'
import { NotFound } from '@/shared/ui/not-found'
import { PageWrapper } from '@/shared/ui/page-wrapper'
import { PermissionGuard } from '@/shared/ui/permission-guard'
import { reportDetailSearchSchema } from '@/shared/zod-schemas'
import { ReportDataWidget } from '@/widgets/report-data-widget'

export const Route = createFileRoute('/_auth/reports/attendance_/item/$reportId')({
  staticData: {
    meta: {
      title: 'Детали отчета посещаемости',
      description: '',
    },
  },
  validateSearch: reportDetailSearchSchema,
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <PermissionGuard permission={PERMISSION_SUB_MODULES.ATTENDANCE_REPORT}>
      <ReportContent />
    </PermissionGuard>
  )
}

function ReportContent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { reportId } = Route.useParams()

  const { data: report, isLoading: isReportLoading, isError } = useAttendanceReportItem(reportId)

  const handleNotFound = useCallback(
    (message: string) => {
      toast.error(message || t('За выбранный период данных не найдено'))
      navigate({ to: '/reports/attendance', replace: true })
    },
    [navigate, t]
  )

  const handleBack = () => {
    navigate({ to: '/reports/attendance' })
  }

  if (isReportLoading) {
    return (
      <PageWrapper title={t('Отчет посещаемости')}>
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      </PageWrapper>
    )
  }

  if (isError || !report) {
    return <NotFound title={t('Ошибка')} description={t('Не удалось загрузить данные отчета')} />
  }

  const period = `${dayjs(report.dateFrom).format('DD.MM.YY')} — ${dayjs(report.dateTo).format('DD.MM.YY')}`
  const pageTitle = `${t('Отчет о посещаемости за период')} ${period}`

  return (
    <PageWrapper title={pageTitle}>
      <Box display="flex" gap={2} alignItems="center">
        <CustomButton
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIos />}
          onClick={handleBack}
        >
          {t('Назад')}
        </CustomButton>
        <Breadcrumbs separator={<ArrowForwardIos sx={{ fontSize: 16 }} />}>
          <Typography color="text.secondary">{t('Отчет посещаемости')}</Typography>
          <Typography color="text.primary">{`${t('Отчет за период')} ${period}`}</Typography>
        </Breadcrumbs>
      </Box>
      <ReportDataWidget
        reportId={reportId}
        routeId={Route.id}
        reportType="attendance"
        useData={useAttendanceReportData}
        useDownload={useDownloadAttendanceReport}
        onNotFound={handleNotFound}
      />
    </PageWrapper>
  )
}
